// Sections de la fiche d'un projet de travaux (lot L4-C) : avancements côte à côte et jamais confondus,
// jalons pondérés (validation par le responsable technique), situations de travaux (liquidations du
// contrat commun, mandat et paiement), rapports de chantier, réceptions et réserves.
import type { Jalon, ProjetTravaux, SituationTravaux } from '@cc/contrats';
import { Bouton, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { useAction } from '../../commun/useAction';
import { declarerAtteint, INVALIDER_TRAVAUX, validerJalon } from './api';
import { francs, libelleJalon, texteAvancementFinancier, texteAvancementPhysique, TON_JALON } from './logique';
import styles from '../achats/achats.module.css';

export function Avancements({ p }: { p: ProjetTravaux }) {
  const af = p.avancement_financier;
  return (
    <section className={styles.avancements} aria-label="Avancements du projet">
      <article className={styles.avancement} aria-labelledby="titre-physique">
        <h3 id="titre-physique">Avancement physique</h3>
        <p className={styles.taux}>{p.avancement_physique.taux.toLocaleString('fr-FR')} %</p>
        <progress className={styles.jauge} max={100} value={p.avancement_physique.taux} aria-label="Avancement physique" />
        <p className={styles.detail}>{texteAvancementPhysique(p.avancement_physique)}</p>
        {p.avancement_physique.justification ? <p className={styles.detail}>Justification : {p.avancement_physique.justification}</p> : null}
      </article>
      <article className={styles.avancement} aria-labelledby="titre-financier">
        <h3 id="titre-financier">Avancement financier</h3>
        <p className={styles.taux}>{af.taux === null ? 'non calculable' : `${af.taux.toLocaleString('fr-FR')} %`}</p>
        <progress className={styles.jauge} max={100} value={af.taux ?? 0} aria-label="Avancement financier" />
        <p className={styles.detail}>{texteAvancementFinancier(af, p.fictif)}</p>
        {af.liquide > 0 ? <p className={styles.detail}>Liquidé en attente de paiement : {francs(af.liquide, p.fictif)}</p> : null}
      </article>
    </section>
  );
}

export function SectionJalons({ p, modifier, valider }: { p: ProjetTravaux; modifier: boolean; valider: boolean }) {
  const atteint = useAction({ executer: (id: string) => declarerAtteint(id), succes: 'Jalon déclaré atteint.', invalider: INVALIDER_TRAVAUX });
  const validation = useAction({ executer: (id: string) => validerJalon(id), succes: 'Jalon validé : l\'avancement physique est recalculé.', invalider: INVALIDER_TRAVAUX });
  const enCours = p.etat === 'en_cours';
  const colonnes: ColonneTableau<Jalon>[] = [
    { id: 'rang', entete: 'Rang', numerique: true, cellule: (j) => j.rang },
    { id: 'libelle', entete: 'Jalon', enTeteLigne: true, masquable: false, cellule: (j) => j.libelle },
    { id: 'ponderation', entete: 'Pondération', numerique: true, cellule: (j) => `${j.ponderation.toLocaleString('fr-FR')} %` },
    { id: 'echeance', entete: 'Échéance', cellule: (j) => j.echeance },
    { id: 'etat', entete: 'État', cellule: (j) => <span className={styles.actesLigne}><PuceEtat ton={TON_JALON[j.etat]} libelle={libelleJalon(j.etat)} />{j.en_retard ? <PuceEtat ton="retard" libelle="En retard" /> : null}</span> },
    { id: 'validation', entete: 'Validation', cellule: (j) => (j.valide ? `${j.valide.par ?? ''} le ${j.valide.le.slice(0, 10)}` : '') },
    {
      id: 'actes', entete: 'Actes', masquable: false, cellule: (j) => (
        <div className={styles.actesLigne}>
          {enCours && j.etat === 'a_venir' && modifier ? <Bouton variante="secondaire" chargement={atteint.enCours} onClick={() => atteint.lancer(j.id)}>Déclarer atteint</Bouton> : null}
          {enCours && j.etat !== 'valide' && valider ? <Bouton variante="principal" chargement={validation.enCours} onClick={() => validation.lancer(j.id)}>Valider le jalon</Bouton> : null}
        </div>
      ),
    },
  ];
  return (
    <section className={styles.bloc} aria-labelledby="titre-jalons">
      <h2 id="titre-jalons">Jalons pondérés</h2>
      {atteint.messageErreur ?? validation.messageErreur ? <p role="alert" className={styles.negatif}>{atteint.messageErreur ?? validation.messageErreur}</p> : null}
      {p.jalons.length === 0 ? <p className={styles.detail}>Aucun jalon défini : la somme des pondérations devra faire 100.</p> : <Tableau legende="Jalons" colonnes={colonnes} lignes={p.jalons} cleLigne={(j) => j.id} cleColonnes="travaux-jalons" />}
    </section>
  );
}

export function SectionSituations({ p }: { p: ProjetTravaux }) {
  const colonnes: ColonneTableau<SituationTravaux>[] = [
    { id: 'rang', entete: 'Situation', enTeteLigne: true, masquable: false, cellule: (s) => (s.rang ? `n° ${s.rang}` : 'hors suivi') },
    { id: 'periode', entete: 'Période', cellule: (s) => (s.periode ? `${s.periode.debut} au ${s.periode.fin}` : '') },
    { id: 'liquidation', entete: 'Liquidation', cellule: (s) => <span className="cc-mono">{s.numero}</span> },
    { id: 'montant', entete: 'Montant', numerique: true, cellule: (s) => francs(s.montant, p.fictif) },
    { id: 'mandat', entete: 'Mandat', cellule: (s) => (s.mandat ? s.mandat.numero : 'À ordonnancer') },
    { id: 'paiement', entete: 'Paiement', cellule: (s) => (s.payee ? <PuceEtat ton="ok" libelle="Payée" /> : <PuceEtat ton="attente" libelle="Non payée" />) },
  ];
  return (
    <section className={styles.bloc} aria-labelledby="titre-situations">
      <h2 id="titre-situations">Situations de travaux</h2>
      <p className={styles.detail}>Chaque situation est une liquidation de la <Lien href="/budget/depense">chaîne de la dépense</Lien> ; seul le payé compte dans l&apos;avancement financier.</p>
      {p.situations.length === 0 ? <p className={styles.detail}>Aucune situation présentée.</p> : <Tableau legende="Situations de travaux" colonnes={colonnes} lignes={p.situations} cleLigne={(s) => s.liquidation_id} cleColonnes="travaux-situations" />}
    </section>
  );
}

export function SectionRapports({ p }: { p: ProjetTravaux }) {
  return (
    <section className={styles.bloc} aria-labelledby="titre-rapports">
      <h2 id="titre-rapports">Rapports de chantier</h2>
      {p.rapports.length === 0 ? <p className={styles.detail}>Aucun rapport de chantier.</p> : (
        <ul className={styles.liste}>
          {p.rapports.map((r) => (
            <li key={r.id}>
              <strong>{r.date}</strong>{r.auteur ? ` (${r.auteur})` : ''}{r.jalon ? `, jalon « ${r.jalon.libelle} »` : ''}{r.effectif !== null ? `, ${r.effectif} personnes` : ''} : {r.contenu}
              {r.photos.length > 0 ? ` ${r.photos.length} photo${r.photos.length > 1 ? 's' : ''}.` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function SectionReceptions({ p, onLever, valider }: { p: ProjetTravaux; onLever: (id: string, description: string) => void; valider: boolean }) {
  return (
    <section className={styles.bloc} aria-labelledby="titre-receptions">
      <h2 id="titre-receptions">Réceptions et réserves</h2>
      {p.receptions.length === 0 ? <p className={styles.detail}>Aucune réception prononcée.</p> : p.receptions.map((r) => (
        <div key={r.id}>
          <h3>{r.type === 'provisoire' ? 'Réception provisoire' : 'Réception définitive'} du {r.date}{r.prononcee_par ? ` par ${r.prononcee_par}` : ''}</h3>
          {r.observations ? <p className={styles.detail}>{r.observations}</p> : null}
          {r.document_id ? <p className={styles.detail}>Procès-verbal : <Lien href={`/documents/${r.document_id}`}>ouvrir le document</Lien></p> : null}
          {r.reserves.length === 0 ? <p className={styles.detail}>Sans réserve.</p> : (
            <ul className={styles.liste}>
              {r.reserves.map((x) => (
                <li key={x.id}>
                  {x.description}{x.echeance ? ` (à lever avant le ${x.echeance})` : ''} : {x.levee ? `levée le ${x.levee.le.slice(0, 10)}, ${x.levee.observation}` : 'ouverte'}{' '}
                  {!x.levee && valider ? <Bouton variante="discret" onClick={() => onLever(x.id, x.description)}>Lever la réserve</Bouton> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      {p.cloture ? <p className={styles.detail}>Projet clos le {p.cloture.le.slice(0, 10)}{p.cloture.observation ? ` : ${p.cloture.observation}` : ''}.</p> : null}
    </section>
  );
}
